import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikProps } from 'formik'
import produce from 'immer'
import { Button } from 'primereact/button'

import { useToastContext } from '../../../../../../contexts/toast'
import { AllowanceCharge } from '../../../../../../data/models'
import { getHandleableError } from '../../../../../../utils/errors/isXiaError'
import {
  requestPutItemAllowanceChargesById,
  useInvoiceItemAllowanceChargeById,
} from '../../../../data'

type Props = {
  invoiceId: string
  itemId: string
  id: string
  formik: FormikProps<any>
}

export const ResetInvoiceItemAllowanceCharges: React.FC<Props> = ({
  invoiceId,
  itemId,
  id,
  formik,
}) => {
  const { t } = useTranslation()
  const { showToast } = useToastContext()
  const { data: invoiceItemAllowanceCharge } =
    useInvoiceItemAllowanceChargeById(invoiceId, itemId, id)

  const [isRequestRunning, setIsRequestRunning] = useState(false)

  async function handleSubmit() {
    // deactivate the button
    setIsRequestRunning(true)

    try {
      // submit form
      submitForm()

      // show success toast
      showToast({
        closable: true,
        severity: 'success',
        summary: t(
          'invoice.invoiceItems.table.edit.dialog.allowanceCharges.table.edit.dialog.reset.messages.success.summary',
          {
            type:
              invoiceItemAllowanceCharge?.ConfirmationAlcType === 'Surcharge'
                ? t('common.codes.alcType.surcharge')
                : t('common.codes.alcType.discount'),
            description: invoiceItemAllowanceCharge?.ConfirmationDescription,
          },
        ),
      })
    } catch (error) {
      let handleableError = getHandleableError(error)
      let errorMessage = handleableError.message

      // show error toast
      showToast({
        closable: true,
        severity: 'error',
        sticky: true,
        summary: errorMessage,
      })
    } finally {
      // activate the button
      setIsRequestRunning(false)
    }
  }

  // submit form
  const submitForm = async () => {
    formik.resetForm()

    // make a copy of the item
    const resetData: AllowanceCharge = produce(
      invoiceItemAllowanceCharge!,
      draft => {
        // visible fields
        draft.ConfirmationPercentage = invoiceItemAllowanceCharge?.Percentage
        draft.ConfirmationAmount = invoiceItemAllowanceCharge?.Amount

        // hidden fields
        draft.Status = 'NOT_SET'
      },
    )

    // put for allowance charge
    return requestPutItemAllowanceChargesById(invoiceId, itemId, id, resetData)
  }

  return (
    <Button
      label={t('common.buttons.reset')}
      onClick={handleSubmit}
      icon="pi pi-replay"
      className="p-button-outlined mr-2 mb-2"
      loading={isRequestRunning}
      type="reset"
    />
  )
}
